import { useEffect, useState } from 'react';
import {
  DEFAULT_COMPANY_DETAILS,
  LEGAL_DOCUMENTS,
  LEGAL_DOCUMENT_KEYS,
  LEGAL_STATUS,
  type CompanyDetails,
  type LegalDocument,
} from '@london-life/contracts';
import { api } from '../lib/api';
import { documentsFor } from './documents';

// /terms, /privacy and /disclaimer are plain pages anyone can read, signed in or not
export const legalDocumentAt = (path: string): LegalDocument | null =>
  LEGAL_DOCUMENT_KEYS.find((key) => LEGAL_DOCUMENTS[key].path === path.replace(/\/$/, '')) ?? null;

const dateOf = (version: string) =>
  new Date(`${version}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export function LegalPage({ document }: { document: LegalDocument }) {
  const meta = LEGAL_DOCUMENTS[document];
  // the company details come from the API (the super admin keeps them up to date); the registered values
  // are shown until they arrive, and if the API cannot be reached
  const [company, setCompany] = useState<CompanyDetails>(DEFAULT_COMPANY_DETAILS);
  useEffect(() => {
    api.companyDetails().then(
      (reply) => setCompany(reply.company),
      () => undefined,
    );
  }, []);
  const content = documentsFor(company)[document];
  return (
    <main className="legal-page">
      <nav className="legal-nav" aria-label="Legal documents">
        <a href="/">London Life</a>
        {LEGAL_DOCUMENT_KEYS.map((key) => (
          <a key={key} href={LEGAL_DOCUMENTS[key].path} aria-current={key === document ? 'page' : undefined}>
            {LEGAL_DOCUMENTS[key].title}
          </a>
        ))}
      </nav>
      <article>
        {LEGAL_STATUS === 'draft' && (
          <p className="legal-draft" role="note">
            Draft for review. This document is not yet in force.
          </p>
        )}
        <h1>{meta.title}</h1>
        <p className="legal-date">Last updated {dateOf(meta.version)}</p>
        <p>{content.intro}</p>
        {content.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            {section.body.map((part, index) =>
              Array.isArray(part) ? (
                <ul key={index}>
                  {part.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p key={index}>{part}</p>
              ),
            )}
          </section>
        ))}
        <p className="legal-contact">
          Questions? Email <a href={`mailto:${company.supportEmail}`}>{company.supportEmail}</a>.
        </p>
        <p className="legal-company">
          London Life is run by {company.companyName}, registered in England and Wales, company number{' '}
          {company.companyNumber}. Registered office: {company.registeredOffice}.
        </p>
      </article>
    </main>
  );
}
